/**
 * Auth Store
 * Manages authentication state and user profile
 *
 * 认证存储
 * 管理认证状态和用户配置文件
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase, setRememberMe, clearRememberMe } from '@/composables/useSupabase'
import type { User as SupabaseUser } from '@supabase/supabase-js'
import type { User } from '@/types/database'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<SupabaseUser | null>(null)
  const profile = ref<User | null>(null)
  const isLoading = ref(true)
  const pendingWorkshopCode = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userId = computed(() => user.value?.id ?? null)
  const nickname = computed(() => profile.value?.nickname ?? '')
  const avatarUrl = computed(() => profile.value?.avatar_url ?? null)
  const isAdmin = computed(() => profile.value?.is_admin ?? false)

  /**
   * Initialize auth state on app load
   * 应用加载时初始化认证状态
   */
  async function init() {
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        user.value = session.user
        await fetchProfile()
      }

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (_event, session) => {
        user.value = session?.user ?? null

        if (session) {
          await fetchProfile()
        } else {
          profile.value = null
        }
      })
    } catch (error) {
      // AbortError from Web Locks API is expected during HMR/page refresh
      // AbortError来自Web Locks API，在HMR/页面刷新时是预期的
      if (error instanceof Error && error.name === 'AbortError') {
        console.debug('Auth lock aborted (normal during HMR)')
      } else {
        console.error('Auth init error:', error)
      }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Fetch user profile from database, create if not exists
   * 从数据库获取用户配置文件，如果不存在则创建
   */
  async function fetchProfile() {
    if (!user.value) return

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.value.id)
      .maybeSingle()

    if (error) {
      console.error('Fetch profile error:', error)
      return
    }

    // If user exists in auth but not in schema, create profile
    // 如果用户存在于auth但不在schema中，创建配置文件
    if (!data && user.value) {
      const defaultNickname = user.value.email?.split('@')[0] || `User_${user.value.id.slice(0, 6)}`
      await ensureProfile(defaultNickname)
      return
    }

    profile.value = data
  }

  /**
   * Ensure user profile exists in database
   * 确保用户配置文件存在于数据库中
   */
  async function ensureProfile(defaultNickname?: string): Promise<User | null> {
    if (!user.value) return null

    // First check if profile exists
    const { data: existing } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.value.id)
      .maybeSingle()

    if (existing) {
      profile.value = existing
      return existing
    }

    // Create new profile
    const nickname = defaultNickname || user.value.email?.split('@')[0] || `User_${user.value.id.slice(0, 6)}`

    const { data: newProfile, error: insertError } = await supabase
      .from('users')
      .insert({
        id: user.value.id,
        nickname,
      })
      .select()
      .single()

    if (insertError) {
      console.error('Create profile error:', insertError)
      return null
    }

    profile.value = newProfile
    return newProfile
  }

  /**
   * Login with email and password
   * 使用邮箱和密码登录
   *
   * @param email - User's email address
   * @param password - User's password
   * @param rememberMe - If true, session persists in localStorage (survives browser close).
   *                     If false, session stored in sessionStorage (cleared on browser close).
   *                     如果为true，会话保存在localStorage中（关闭浏览器后保留）。
   *                     如果为false，会话保存在sessionStorage中（关闭浏览器时清除）。
   */
  async function loginWithEmail(
    email: string,
    password: string,
    rememberMe = true
  ) {
    // Set the remember me preference BEFORE login so storage adapter uses correct storage
    // 在登录前设置"记住我"偏好，以便存储适配器使用正确的存储
    setRememberMe(rememberMe)

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) throw error
  }

  /**
   * Register with email and password
   * 使用邮箱和密码注册
   */
  async function register(email: string, password: string, nickname: string) {
    // Default to remember me for new registrations
    // 新注册默认记住我
    setRememberMe(true)

    // Sign up user
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) throw error

    // Create profile if user was created
    if (data.user) {
      const { error: profileError } = await supabase.from('users').insert({
        id: data.user.id,
        nickname,
      })

      if (profileError) {
        console.error('Create profile error:', profileError)
      }
    }
  }

  /**
   * Login with WeChat OAuth
   * 使用微信OAuth登录
   */
  async function loginWithWeChat() {
    // Default to remember me for OAuth logins
    // OAuth登录默认记住我
    setRememberMe(true)

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'wechat' as any, // WeChat provider may not be in types
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })

    if (error) throw error
  }

  /**
   * Logout
   * 退出登录
   */
  async function logout() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error

    // Clear remember me preference on logout
    // 注销时清除"记住我"偏好
    clearRememberMe()

    user.value = null
    profile.value = null
  }

  /**
   * Update user profile
   * 更新用户配置文件
   */
  async function updateProfile(updates: Partial<User>) {
    if (!user.value) return

    const { error } = await supabase
      .from('users')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.value.id)

    if (error) throw error

    await fetchProfile()
  }

  /**
   * Store pending workshop code before auth redirect
   * 在认证重定向前存储待处理的工作坊代码
   */
  function setPendingWorkshopCode(code: string) {
    pendingWorkshopCode.value = code
    sessionStorage.setItem('pendingWorkshopCode', code)
  }

  /**
   * Get pending workshop code after auth completes
   * 认证完成后获取待处理的工作坊代码
   */
  function getPendingWorkshopCode(): string | null {
    return (
      pendingWorkshopCode.value ||
      sessionStorage.getItem('pendingWorkshopCode')
    )
  }

  /**
   * Clear pending workshop code after joining
   * 加入后清除待处理的工作坊代码
   */
  function clearPendingWorkshopCode() {
    pendingWorkshopCode.value = null
    sessionStorage.removeItem('pendingWorkshopCode')
  }

  return {
    // State
    user,
    profile,
    isLoading,
    pendingWorkshopCode,
    // Getters
    isAuthenticated,
    userId,
    nickname,
    avatarUrl,
    isAdmin,
    // Actions
    init,
    fetchProfile,
    ensureProfile,
    loginWithEmail,
    register,
    loginWithWeChat,
    logout,
    updateProfile,
    setPendingWorkshopCode,
    getPendingWorkshopCode,
    clearPendingWorkshopCode,
  }
})
